import { Select } from "@mui/material";
import * as React from "react";

import { Button } from "@/shared/foundation/primitives";
import type {
  Joint,
  JointBinding,
} from "@/shared/state/visualization/schema/v1/three-d";

import {
  assumedUnit,
  bindingProblem,
  joinedPathOf,
  makeBinding,
} from "./jointBindingDraft";
import styles from "./JointBindingRow.module.css";
import { resolveUnit, type ResolvedUnit } from "./jointBindingUnits";
import { TopicPathAutocomplete } from "./TopicPathAutocomplete";
import { useTopicPathParts } from "./useTopicPathParts";

interface JointBindingRowProps {
  joint: Joint;
  /** What this joint is declared to be driven by, or nothing. */
  binding: JointBinding | undefined;
  /** Emits the joint's new declaration, or `undefined` to remove it. */
  onChange: (binding: JointBinding | undefined) => void;
  orgId?: string;
}

/**
 * One joint's own topic and message path, as a single path expression.
 *
 * `/joint_states.position[3]` — topic, field, and which element of it, in one
 * value. The index lives in the text rather than in a control beside it because
 * that spelling is already what the rest of the system speaks: a
 * `MessagePathSlice` narrows an array node to one element, `findNodesByParts`
 * copies the requested bounds onto whatever matched, and the data server
 * resolves `position[3:4]` to a scalar. So the picker only has to let it be
 * typed — and the choice stays visible in the value instead of behind a mode.
 *
 * ## The array trap, and where the guard now lives
 *
 * Binding an un-narrowed array path does not fail. The data server takes
 * `valuesAtMessagePath(...)[0]`, so `/joint_states.position` binds **element 0
 * to every joint** — a robot where every joint follows joint 0, which looks
 * perfectly bound and is completely wrong.
 *
 * The guard is therefore validation, and it turns on what the field catalogue
 * says the field is:
 *
 * - **Known array** — a path with no index is refused, and the row says an index
 *   is required. Nothing is emitted, so the joint cannot be *left* in that state.
 * - **Known scalar** — nothing to index; the bare path is accepted as it stands.
 * - **Type unknown** — the catalogue carries no type for this field (an API that
 *   predates the widening, or a field it has never seen). The bare path is
 *   accepted, and the row says plainly that if the field turns out to be an
 *   array this reads element 0. Blocking here would refuse the normal
 *   ingest-time case, where no recording of this robot exists yet at all.
 * - **Not asked yet** — the catalogue query is still in flight, which is not an
 *   answer and is therefore not narrated as one. A row opened on a saved
 *   scalar binding renders before that fetch resolves, and claiming "the
 *   catalogue does not report this field's type" in that gap was a warning that
 *   appeared and then retracted itself a round trip later.
 */
export function JointBindingRow({
  joint,
  binding,
  onChange,
  orgId,
}: JointBindingRowProps) {
  const [text, setText] = React.useState(() => joinedPathOf(binding));
  /**
   * Whether the user has touched *this* row.
   *
   * Nothing is republished until they have, and that is a data-safety rule
   * rather than an optimization. This row rebuilds a declaration from the parts
   * it can parse out of one path expression — topic, field, index, scale — so
   * anything the format carries that those four cannot express is lost in the
   * rebuild. `elementNamedBy` is exactly that today: this UI no longer authors
   * it, but the extension carries it, the resolver consumes it, and a converter,
   * an earlier build or a future bulk action may have written it.
   *
   * Republishing on mount would therefore strip it from every loaded binding —
   * and since the save writes the **complete** set, merely opening the wizard
   * and pressing Save would destroy it in the stored robot description. Not
   * authoring a form is not the same as destroying it.
   *
   * Gating on "edited" fixes the whole class rather than that one field: any
   * future attribute `makeBinding` cannot round-trip survives an untouched row
   * by construction.
   */
  const [edited, setEdited] = React.useState(false);

  /**
   * The unit the user picked for this joint, or `undefined` to take the field
   * catalogue's word for it.
   *
   * Held rather than derived because the catalogue is the thing being
   * overridden: at ingest time it is usually absent altogether — a URDF is
   * routinely ingested before the robot has ever been driven — and when it is
   * present it is whatever the recording's producer wrote. The person looking
   * at the robot is the better authority, and this is where they say so.
   */
  const [unitOverride, setUnitOverride] = React.useState<string | undefined>(
    undefined,
  );

  /**
   * The last declaration this row emitted.
   *
   * The row edits text locally — a half-typed path is not a binding — so its own
   * emissions must not be mistaken for someone else changing the binding out
   * from under it. Anything arriving that this row did not emit (a reset, a
   * future bulk action) replaces the local edit; anything it did emit is already
   * on screen.
   */
  const emitted = React.useRef(binding);
  React.useEffect(
    function adoptExternalChanges() {
      if (binding === emitted.current) {
        return;
      }
      emitted.current = binding;
      setText(joinedPathOf(binding));
      // A declaration that arrived from outside is not this row's edit, so the
      // row goes back to preserving it verbatim — unit choice included, since
      // that choice was about the path this row no longer holds.
      setEdited(false);
      setUnitOverride(undefined);
    },
    [binding],
  );

  const parts = useTopicPathParts(text, orgId);
  const problem = bindingProblem(
    parts.topic,
    parts.messageField,
    parts.arity,
    parts.element,
  );
  /**
   * The unit this row is writing its declaration in.
   *
   * One value drives both the picker's displayed unit and the `scale` written
   * into the binding, so the two cannot disagree. Which units even exist is the
   * *joint's* business, not the field's: a joint that slides is never offered
   * degrees, and a catalogue that reports one for it is ignored rather than
   * applied. See {@link resolveUnit}.
   *
   * The declaration's own `scale` is one of the inputs, and it has to be: it is
   * the conversion in force, so a picker that ignored it would show `rad` over a
   * binding applying π/180 — and selecting `rad` on a row already reading `rad`
   * emits no change at all, which is a degrees factor that cannot be taken back
   * off through the only control that writes it.
   */
  const units = resolveUnit(
    joint,
    unitOverride,
    parts.field?.unit,
    binding?.scale,
  );
  const scale = units.selected.scale;

  // Built in an effect rather than in the change handler, because the field's
  // type is only known once the catalogue has answered: a path typed before that
  // fetch resolves would otherwise be judged against "unknown" and never
  // re-judged once the answer said "array".
  React.useEffect(
    function publishDeclaration() {
      // Preserve on read, author only on write. An untouched row leaves the
      // declaration it was given exactly as it was stored — see `edited`.
      if (!edited) {
        return;
      }
      const declaration =
        problem === undefined
          ? makeBinding({
              topic: parts.topic,
              messagePath: parts.messageField,
              ...(parts.element !== undefined
                ? { element: parts.element }
                : {}),
              // Always a number now, and `makeBinding` omits it when it is 1:
              // the extension's default is 1, so writing it would say nothing
              // the absence does not.
              scale,
            })
          : undefined;
      if (sameBinding(declaration, emitted.current)) {
        return;
      }
      emitted.current = declaration;
      onChange(declaration);
    },
    [
      edited,
      parts.topic,
      parts.messageField,
      parts.element,
      problem,
      scale,
      onChange,
    ],
  );

  const onEdit = React.useCallback((next: string) => {
    setEdited(true);
    setText(next);
  }, []);

  const onUnitChange = React.useCallback((next: string) => {
    // Picking a unit is an edit in its own right, for the same reason clearing
    // is: it changes the number this row would write. Leaving `edited` unset
    // would leave a row that had *only* had its unit changed reading as
    // untouched, so the save would write the declaration the file already had
    // and the picker would look like it did nothing.
    setEdited(true);
    setUnitOverride(next);
  }, []);

  const onClear = () => {
    // Clearing is itself an edit — an explicit "this joint has no declaration",
    // which is a thing to publish rather than to preserve. Leaving `edited`
    // unset would leave the row reading as untouched, and a save writes the
    // complete set from what was loaded: the declaration would come straight
    // back, silently, and the button would look like it did nothing.
    setEdited(true);
    setText("");
    emitted.current = undefined;
    onChange(undefined);
  };

  return (
    <div className={styles.binding}>
      <div className={styles.field}>
        <TopicPathAutocomplete
          ariaLabel={`Data source for ${joint.name}`}
          onChange={onEdit}
          orgId={orgId}
          placeholder="/joint_states.position[0]"
          value={text}
        />
        {/* Under the path, not beside it. Both on one line left each of them
            around 8rem in the sidebar's default width — a picker too narrow to
            read and a field too narrow to see a path in — and the path is the
            one that needs the room, so the unit takes the line below and the
            path takes the whole of its own.

            The clear control shares that second line: it acts on the row as a
            whole, so it belongs with the row's other control rather than
            stranded on a line of its own between the path and the notes about
            it. Trailing-aligned, away from the picker, since it is the one
            destructive thing here. */}
        <div className={styles.unitRow}>
          {/* The unit the published values are in, which is a property of the
              data rather than of the joint — so it is stated here rather than
              inferred from the field's name. `native` keeps it one element with
              its options in the document: a robot's sidebar carries one of
              these per row, and twenty portalled menus is twenty subtrees to
              mount. */}
          <Select
            className={styles.unit}
            inputProps={{ "aria-label": `Units for ${joint.name}` }}
            native
            onChange={(event) => onUnitChange(event.target.value)}
            size="small"
            value={units.selected.unit}
          >
            {units.options.map((option) => (
              <option key={option.unit} value={option.unit}>
                {option.unit}
              </option>
            ))}
          </Select>
          {text !== "" && (
            // Named for the joint it acts on: a robot's sidebar carries one of
            // these per row, and "Clear" alone says nothing about which.
            <Button
              aria-label={`Clear the binding for ${joint.name}`}
              className={styles.clear}
              onClick={onClear}
              size="small"
            >
              Clear
            </Button>
          )}
        </div>
      </div>
      {problem === "index_required" && (
        <span className={styles.warning}>
          {`${parts.messageField} is ${parts.dataType ?? "an array"} — say which element drives this joint, e.g. ${parts.messageField}[0].`}
        </span>
      )}
      {binding !== undefined && (
        <span className={styles.note}>
          {describe(joint, binding, parts.dataType, edited ? units : undefined)}
        </span>
      )}
      {binding !== undefined &&
        binding.element === undefined &&
        binding.elementNamedBy === undefined &&
        parts.arity === "unknown" &&
        (parts.typeConflict ? (
          <span className={styles.warning}>
            This topic&apos;s recordings disagree about this field&apos;s type.
            If it is an array in the one you open, this reads element 0 — write
            an index to be sure.
          </span>
        ) : (
          <span className={styles.warning}>
            The catalogue does not report this field&apos;s type. If it is an
            array, this reads element 0 — write an index, e.g.{" "}
            {`${parts.messageField}[0]`}, to be sure.
          </span>
        ))}
    </div>
  );
}

/**
 * What this row is declaring, in a sentence, units and all.
 *
 * @param dataType the field's source spelling, when the catalogue reports one
 *   that says something — a `data_type` echoing the canonical type is not a
 *   spelling to show, and `(array)` beside a path adds nothing anyway
 * @param units the unit this row resolved, or `undefined` for an untouched row
 */
function describe(
  joint: Joint,
  binding: JointBinding,
  dataType: string | undefined,
  units: ResolvedUnit | undefined,
): string {
  const type = dataType === undefined ? "" : ` (${dataType})`;
  return `${binding.topic}.${binding.messagePath}${selectorOf(binding)}${type}${unitsOf(joint, binding, units)}`;
}

/**
 * The units half of the note: what conversion is being applied, and on whose say-so.
 *
 * The provenance is not decoration. "Scaled by 0.0175" alone leaves a user
 * unable to tell a factor they chose from one a catalogue handed them, and the
 * two need different fixes — one is a mistake on this row, the other is a
 * mis-ingested recording.
 *
 * Untouched rows (`units === undefined`) get the plain wording and no claim
 * about provenance at all. Their `scale` is whatever the file already declared,
 * which this row did not author and cannot attribute: a converter, an earlier
 * build or a hand-written extension may have put it there.
 */
function unitsOf(
  joint: Joint,
  binding: JointBinding,
  units: ResolvedUnit | undefined,
): string {
  const si = assumedUnit(joint);
  if (units === undefined) {
    return binding.scale === undefined
      ? `, assumed to be in ${si}`
      : `, scaled by ${binding.scale} into ${si}`;
  }
  // No `scale` on the binding means the selected unit *is* the SI one — a
  // scale of 1, which the extension defaults to and `makeBinding` omits.
  if (binding.scale === undefined) {
    return units.source === "assumed"
      ? `, assumed to be in ${si}`
      : `, in ${si}, ${attribution(units)}`;
  }
  return `, scaled by ${binding.scale} into ${si} from ${units.selected.unit}, ${attribution(units)}`;
}

function attribution(units: ResolvedUnit): string {
  switch (units.source) {
    case "override":
      return "the unit set here";
    case "binding":
      // Named apart from the catalogue's, because the fixes differ: this factor
      // is one somebody saved onto this joint and can take back off here, while
      // the catalogue's came with the recording.
      return "the unit already saved for this joint";
    default:
      return "the field's declared unit";
  }
}

function selectorOf(binding: JointBinding): string {
  if (binding.element !== undefined) {
    return `[${binding.element}]`;
  }
  if (binding.elementNamedBy !== undefined) {
    // Not authored here any more, but a declaration written by a converter or by
    // a bulk action still has to read back honestly.
    return ` — the element named ${binding.elementNamedBy.key} in ${binding.elementNamedBy.path}`;
  }
  return "";
}

/** Whether two declarations say the same thing, so nothing re-emits on a rerender. */
function sameBinding(
  a: JointBinding | undefined,
  b: JointBinding | undefined,
): boolean {
  if (a === undefined || b === undefined) {
    return a === b;
  }
  return (
    a.topic === b.topic &&
    a.messagePath === b.messagePath &&
    a.element === b.element &&
    a.scale === b.scale &&
    a.elementNamedBy?.path === b.elementNamedBy?.path &&
    a.elementNamedBy?.key === b.elementNamedBy?.key
  );
}
